import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { http, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import {
  ScanLine, DollarSign, Image as ImageIcon, FileSignature,
  Plus, AlertCircle, MessageSquare, Settings as Cog, Download,
  Loader2, ExternalLink, CheckCircle2, Clock,
} from "lucide-react";
import ImageField from "@/components/ImageField";

const TABS = [
  { k: "overview", l: "Overview" },
  { k: "gallery", l: "My Gallery" },
  { k: "agreement", l: "Agreement" },
  { k: "requests", l: "Request More" },
  { k: "sales", l: "Report Sale" },
  { k: "tickets", l: "Issues" },
  { k: "commissions", l: "Commissions" },
  { k: "settings", l: "Settings" },
];

export default function Partner() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [dash, setDash] = useState(null);

  const reload = async () => {
    try { const { data } = await http.get("/partner/dashboard"); setDash(data); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to load dashboard."); }
  };

  useEffect(() => {
    if (user && (user.role === "partner" || user.role === "admin")) reload();
  }, [user]);

  if (loading) return <Layout><div className="py-32 text-center text-[#8a857d]"><Loader2 className="animate-spin inline-block mr-2" size={16} />Loading…</div></Layout>;
  if (!user) return <Navigate to="/login" state={{ next: "/partner" }} replace />;
  if (user.role !== "partner" && user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <Layout>
      <section className="bg-[#050505] pt-16 pb-8">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10">
          <div className="font-ui text-[11px] tracking-[0.32em] text-[#C9A055] mb-3">Host Dashboard</div>
          <h1 className="font-display text-4xl md:text-6xl text-[#F4F1EA] leading-[0.95] mb-2">
            {(dash?.user?.business_name || user.name || "Host").toUpperCase()}.
          </h1>
          <p className="font-script text-[#C9A055] text-2xl">Your wall. Your impact. Your earnings.</p>
        </div>
      </section>

      {/* Pending action banners */}
      {dash?.pending_offer && (
        <Banner
          icon={<FileSignature size={18} />}
          label="You have a new offer ready"
          desc="Review your terms and accept to receive your agreement."
          actionLabel="Open Offer →"
          onClick={() => nav(`/partner/offer/${dash.pending_offer.id}`)}
          testid="banner-offer"
        />
      )}
      {dash?.pending_agreement && (
        <Banner
          icon={<FileSignature size={18} />}
          label="Sign your agreement to activate"
          desc="Just a quick signature and you're live."
          actionLabel="Sign Now →"
          onClick={() => nav(`/partner/agreement/${dash.pending_agreement.id}`)}
          testid="banner-agreement"
        />
      )}

      <section className="bg-[#050505] pb-24">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap gap-2 border-b border-white/10 mb-6">
            {TABS.map(t => (
              <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
                className={`px-4 py-3 font-ui text-[10px] tracking-[0.22em] uppercase border-b-2 transition-colors ${tab === t.k ? "border-[#C9A055] text-[#C9A055]" : "border-transparent text-[#8a857d] hover:text-[#F4F1EA]"}`}>
                {t.l}
              </button>
            ))}
          </div>

          {!dash ? <Spinner /> : (
            <>
              {tab === "overview" && <Overview d={dash} />}
              {tab === "gallery" && <Gallery d={dash} />}
              {tab === "agreement" && <AgreementPane d={dash} reload={reload} />}
              {tab === "requests" && <Requests reload={reload} />}
              {tab === "sales" && <Sales d={dash} reload={reload} />}
              {tab === "tickets" && <Tickets reload={reload} />}
              {tab === "commissions" && <Commissions />}
              {tab === "settings" && <SettingsPane onSaved={reload} />}
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}

function Banner({ icon, label, desc, actionLabel, onClick, testid }) {
  return (
    <div className="bg-[#0a0a0a] border-y border-[#C9A055]/30">
      <div className="max-w-[1480px] mx-auto px-6 lg:px-10 py-4 flex items-center gap-4 flex-wrap">
        <span className="text-[#C9A055]">{icon}</span>
        <div className="flex-1">
          <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055]">{label}</div>
          <div className="font-body text-[13px] text-[#B5B0A7]">{desc}</div>
        </div>
        <button onClick={onClick} className="btn-gold" data-testid={testid}>{actionLabel}</button>
      </div>
    </div>
  );
}

function Spinner() { return <div className="py-12 text-center text-[#8a857d]"><Loader2 className="animate-spin inline-block mr-2" size={16} />Loading…</div>; }

function Overview({ d }) {
  const s = d.stats;
  const [analytics, setAnalytics] = useState(null);
  useEffect(() => { http.get("/partner/scan-analytics?days=30").then(({ data }) => setAnalytics(data)); }, []);
  const maxScans = (analytics?.timeseries || []).reduce((m, p) => Math.max(m, p.scans), 0) || 1;

  return (
    <div className="space-y-6" data-testid="overview-pane">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat icon={<ScanLine size={22} />} label="Total Scans" value={s.total_scans} />
        <Stat icon={<DollarSign size={22} />} label="Total Sales" value={`$${s.total_sales.toFixed(2)}`} />
        <Stat icon={<DollarSign size={22} />} label="Commission Earned" value={`$${s.commission_earned.toFixed(2)}`} />
        <Stat icon={<DollarSign size={22} />} label="Pending Payout" value={`$${s.commission_pending.toFixed(2)}`} />
      </div>
      <div className="grid md:grid-cols-3 gap-4 text-[13px]">
        <Card label="Commission Rate" value={`${(d.commission_rate * 100).toFixed(1)}%`} />
        <Card label="Active Pieces" value={s.active_pieces} />
        <Card label="Agreement" value={d.current_agreement ? "Signed" : "—"} />
      </div>

      {/* 30-day scan chart */}
      <div className="border border-white/10 bg-[#0a0a0a] p-5" data-testid="scan-chart">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055]">Last 30 Days · Scans</div>
            <div className="font-display text-2xl text-[#F4F1EA] mt-1">{analytics?.total_scans ?? "—"} total</div>
          </div>
        </div>
        {analytics?.timeseries?.length ? (
          <div className="flex items-end gap-1 h-32" data-testid="scan-chart-bars">
            {analytics.timeseries.map((p) => (
              <div key={p.date} className="flex-1 group relative" title={`${p.date}: ${p.scans} scans`}>
                <div className="bg-[#C9A055]/70 hover:bg-[#C9A055] transition-colors"
                  style={{ height: `${(p.scans / maxScans) * 100}%`, minHeight: 2 }} />
                <div className="hidden group-hover:block absolute -top-7 left-1/2 -translate-x-1/2 bg-[#0a0a0a] border border-[#C9A055]/40 px-2 py-0.5 text-[10px] text-[#C9A055] whitespace-nowrap">
                  {p.date.slice(5)}: {p.scans}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[12px] text-[#8a857d] italic">No scans recorded in the last 30 days.</p>
        )}
        {analytics?.by_artwork?.length > 0 && (
          <div className="mt-5 border-t border-white/10 pt-4">
            <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mb-2">Top pieces by scans</div>
            <div className="space-y-1">
              {analytics.by_artwork.slice(0, 5).map(a => (
                <div key={a.artwork_id} className="flex justify-between items-center text-[13px]">
                  <span className="text-[#E8E4DC] truncate">{a.title}</span>
                  <span className="text-[#C9A055] tabular-nums">{a.scans}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
function Stat({ icon, label, value }) {
  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-5">
      <div className="text-[#C9A055] mb-3">{icon}</div>
      <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055]">{label}</div>
      <div className="font-display text-3xl text-[#F4F1EA] mt-1">{value}</div>
    </div>
  );
}
function Card({ label, value }) {
  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-5">
      <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d]">{label}</div>
      <div className="font-display text-2xl text-[#F4F1EA] mt-1">{value}</div>
    </div>
  );
}

function Gallery({ d }) {
  if (!d.assignments?.length) {
    return <Empty icon={<ImageIcon size={28} />} title="No artwork assigned yet" body="Once Mickey assigns your first pieces, they'll appear here with QR codes, scans, and sales." />;
  }
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {d.assignments.map(a => (
        <div key={a.id} className="border border-white/10 bg-[#0a0a0a] p-4" data-testid={`assign-${a.id}`}>
          {a.artwork?.main_image && <img src={a.artwork.main_image} alt="" className="w-full h-48 object-cover" />}
          <div className="mt-3">
            <div className="font-display text-lg text-[#F4F1EA]">{a.artwork?.title || "—"}</div>
            <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055] mt-1">{a.status?.replace("_", " ")}</div>
            <div className="text-[11px] text-[#8a857d] mt-2">Assigned {a.assigned_at ? new Date(a.assigned_at).toLocaleDateString() : "—"}</div>
            {a.notes && <p className="text-[12px] text-[#B5B0A7] mt-2 italic">&ldquo;{a.notes}&rdquo;</p>}
          </div>
        </div>
      ))}
      <div className="border border-white/10 bg-[#0a0a0a] p-4">
        <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055] mb-3">Your QR Codes</div>
        {d.qr_codes?.length ? (
          <div className="space-y-2">
            {d.qr_codes.map(q => {
              const png = `${API_BASE}/api/qr/png/${q.qr_code}?base_url=${encodeURIComponent(window.location.origin)}`;
              return (
                <a key={q.id} href={png} download={`qr-${q.qr_code}.png`} className="flex items-center gap-3 hover:bg-white/[0.02] p-2">
                  <img src={png} alt="qr" className="w-12 h-12 bg-white p-1" />
                  <div className="flex-1">
                    <div className="text-[13px] text-[#F4F1EA]">{q.artwork?.title || q.qr_code}</div>
                    <div className="text-[10px] text-[#8a857d]">{q.scan_count || 0} scans · {q.purchase_count || 0} sales</div>
                  </div>
                  <Download size={14} className="text-[#8a857d]" />
                </a>
              );
            })}
          </div>
        ) : <p className="text-[12px] text-[#8a857d]">No QR codes assigned yet.</p>}
      </div>
    </div>
  );
}

function AgreementPane({ d }) {
  const cur = d.current_agreement;
  const pending = d.pending_agreement;
  const nav = useNavigate();
  if (!cur && !pending) {
    return <Empty icon={<FileSignature size={28} />} title="No agreement yet"
      body="Once Mickey sends you an offer and you accept, your agreement will appear here." />;
  }
  return (
    <div className="space-y-6" data-testid="agreement-pane">
      {pending && (
        <div className="border border-amber-700/40 bg-amber-900/10 p-6">
          <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-amber-300 mb-2">Awaiting Signature</div>
          <p className="text-[#B5B0A7] mb-4">You accepted your offer — sign your agreement to go live.</p>
          <button className="btn-gold" onClick={() => nav(`/partner/agreement/${pending.id}`)}>
            Sign Agreement →
          </button>
        </div>
      )}
      {cur && (
        <div className="border border-white/10 bg-[#0a0a0a] p-6">
          <div className="flex justify-between items-start gap-4 mb-4 flex-wrap">
            <div>
              <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055]">Signed</div>
              <div className="font-display text-2xl text-[#F4F1EA] mt-1">Simple Truths Host Agreement</div>
              <div className="text-[12px] text-[#8a857d] mt-1">
                Signed {new Date(cur.signed_at).toLocaleDateString()} by {cur.signer_name}
              </div>
            </div>
            <a href={`${API_BASE}/api/partner/agreements/${cur.id}/pdf`} target="_blank" rel="noreferrer"
              className="btn-outline inline-flex items-center gap-2"><Download size={13} /> Download PDF</a>
          </div>
          <pre className="whitespace-pre-wrap font-body text-[13px] text-[#B5B0A7] max-h-96 overflow-y-auto border-t border-white/10 pt-4">
            {cur.content_rendered}
          </pre>
        </div>
      )}
    </div>
  );
}

function Requests({ reload }) {
  const [list, setList] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ request_type: "more_pieces", quantity: 1, reason: "" });
  useEffect(() => { http.get("/partner/requests").then(({ data }) => setList(data)); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await http.post("/partner/requests", form);
      toast.success("Request sent.");
      setShow(false); setForm({ request_type: "more_pieces", quantity: 1, reason: "" });
      const { data } = await http.get("/partner/requests"); setList(data);
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };
  return (
    <div className="space-y-4" data-testid="requests-pane">
      <button className="btn-gold inline-flex items-center gap-2" onClick={() => setShow(!show)} data-testid="req-new">
        <Plus size={14} /> New Request
      </button>
      {show && (
        <form onSubmit={submit} className="border border-white/10 bg-[#0a0a0a] p-5 space-y-3">
          <Sel label="What do you need?" value={form.request_type} onChange={v => setForm({ ...form, request_type: v })}
            opts={[
              ["more_pieces", "More pieces"], ["specific", "Specific artwork"],
              ["random", "Random curated set"], ["seasonal", "Seasonal refresh"],
              ["replacement", "Replacement piece"], ["larger", "Larger display"],
            ]} />
          <FieldRow label="How many?">
            <input type="number" min={1} max={20} value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value || "1") })}
              className="stc-input w-32" />
          </FieldRow>
          <FieldRow label="Tell us more">
            <textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className="stc-input w-full font-body" />
          </FieldRow>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShow(false)} className="btn-outline">Cancel</button>
            <button type="submit" className="btn-gold" data-testid="req-submit">Send Request</button>
          </div>
        </form>
      )}
      <RowList items={list} testid="req-row"
        render={(r) => (
          <>
            <div className="font-ui text-[10px] uppercase tracking-[0.22em] text-[#C9A055]">{r.request_type.replace("_", " ")}</div>
            <div className="text-[13px] text-[#F4F1EA]">Qty {r.quantity} · {r.status}</div>
            {r.reason && <div className="text-[12px] text-[#B5B0A7] italic">&ldquo;{r.reason}&rdquo;</div>}
            <div className="text-[10px] text-[#8a857d]">{new Date(r.created_at).toLocaleDateString()}</div>
          </>
        )}
        empty="No requests yet." />
    </div>
  );
}

function Sales({ d, reload }) {
  const [list, setList] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    artwork_id: "", sale_amount: "", buyer_name: "", buyer_email: "",
    payment_collected_by: "host", notes: "",
  });
  useEffect(() => { http.get("/partner/sales").then(({ data }) => setList(data)); }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.artwork_id || !form.sale_amount) { toast.error("Pick artwork and amount."); return; }
    try {
      await http.post("/partner/sales", { ...form, sale_amount: parseFloat(form.sale_amount) });
      toast.success("Sale reported. Pending approval.");
      setShow(false); setForm({ artwork_id: "", sale_amount: "", buyer_name: "", buyer_email: "", payment_collected_by: "host", notes: "" });
      const { data } = await http.get("/partner/sales"); setList(data); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };
  const myArt = d.assignments || [];
  return (
    <div className="space-y-4" data-testid="sales-pane">
      <button className="btn-gold inline-flex items-center gap-2" onClick={() => setShow(!show)} data-testid="sale-new">
        <Plus size={14} /> Report A Sale
      </button>
      {show && (
        <form onSubmit={submit} className="border border-white/10 bg-[#0a0a0a] p-5 space-y-3">
          <Sel label="Which artwork sold?" value={form.artwork_id} onChange={v => setForm({ ...form, artwork_id: v })}
            opts={[["", "Select…"], ...myArt.map(a => [a.artwork_id, a.artwork?.title || a.artwork_id])]} />
          <FieldRow label="Sale price *">
            <input required type="number" step="0.01" min="0" value={form.sale_amount}
              onChange={(e) => setForm({ ...form, sale_amount: e.target.value })} className="stc-input w-40" />
          </FieldRow>
          <div className="grid sm:grid-cols-2 gap-3">
            <FieldRow label="Buyer name">
              <input value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} className="stc-input w-full" />
            </FieldRow>
            <FieldRow label="Buyer email">
              <input value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })} className="stc-input w-full" />
            </FieldRow>
          </div>
          <Sel label="Payment collected by" value={form.payment_collected_by} onChange={v => setForm({ ...form, payment_collected_by: v })}
            opts={[["host", "Host"], ["stc", "Simple Truths Club"], ["other", "Other"]]} />
          <FieldRow label="Notes">
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="stc-input w-full font-body" />
          </FieldRow>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShow(false)} className="btn-outline">Cancel</button>
            <button type="submit" className="btn-gold" data-testid="sale-submit">Submit Sale</button>
          </div>
        </form>
      )}
      <RowList items={list} testid="sale-row"
        render={(s) => (
          <>
            <div className="flex justify-between items-start gap-3 flex-wrap">
              <div>
                <div className="font-ui text-[10px] uppercase tracking-[0.22em] text-[#C9A055]">{s.status}</div>
                <div className="text-[14px] text-[#F4F1EA]">${parseFloat(s.sale_amount).toFixed(2)} · {s.buyer_name || "—"}</div>
                <div className="text-[11px] text-[#8a857d]">{new Date(s.created_at).toLocaleDateString()} · commission ${parseFloat(s.commission_amount || 0).toFixed(2)}</div>
              </div>
            </div>
            {s.notes && <div className="text-[12px] text-[#B5B0A7] italic mt-1">&ldquo;{s.notes}&rdquo;</div>}
          </>
        )}
        empty="No reported sales yet." />
    </div>
  );
}

function Tickets({ reload }) {
  const [list, setList] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "qr_damaged", message: "" });
  useEffect(() => { http.get("/partner/tickets").then(({ data }) => setList(data)); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await http.post("/partner/tickets", form);
      toast.success("Issue reported.");
      setShow(false); setForm({ subject: "", category: "qr_damaged", message: "" });
      const { data } = await http.get("/partner/tickets"); setList(data); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };
  return (
    <div className="space-y-4" data-testid="tickets-pane">
      <button className="btn-gold inline-flex items-center gap-2" onClick={() => setShow(!show)} data-testid="ticket-new">
        <AlertCircle size={14} /> Report An Issue
      </button>
      {show && (
        <form onSubmit={submit} className="border border-white/10 bg-[#0a0a0a] p-5 space-y-3">
          <FieldRow label="Subject *">
            <input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="stc-input w-full" />
          </FieldRow>
          <Sel label="Category" value={form.category} onChange={v => setForm({ ...form, category: v })}
            opts={[
              ["qr_damaged", "QR code damaged"], ["frame_damaged", "Frame damaged"],
              ["art_damaged", "Artwork damaged"], ["art_missing", "Artwork missing"],
              ["piece_sold", "Piece sold"], ["need_replacement", "Need replacement"],
              ["need_signage", "Need signage"], ["commission_question", "Commission question"],
              ["other", "Other"],
            ]} />
          <FieldRow label="Message *">
            <textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="stc-input w-full font-body" />
          </FieldRow>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShow(false)} className="btn-outline">Cancel</button>
            <button type="submit" className="btn-gold" data-testid="ticket-submit">Submit</button>
          </div>
        </form>
      )}
      <RowList items={list} testid="ticket-row"
        render={(t) => (
          <>
            <div className="font-ui text-[10px] uppercase tracking-[0.22em] text-[#C9A055]">{t.category.replace("_", " ")} · {t.status}</div>
            <div className="text-[14px] text-[#F4F1EA]">{t.subject}</div>
            <div className="text-[12px] text-[#B5B0A7] mt-1">{t.message}</div>
            <div className="text-[10px] text-[#8a857d]">{new Date(t.created_at).toLocaleDateString()}</div>
          </>
        )}
        empty="No issues reported. Good wall!" />
    </div>
  );
}

function Commissions() {
  const [rows, setRows] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [report, setReport] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  useEffect(() => {
    http.get("/partner/commissions").then(({ data }) => setRows(data));
    http.get("/partner/payouts").then(({ data }) => setPayouts(data));
  }, []);
  useEffect(() => {
    http.get(`/partner/monthly-report?month=${month}`).then(({ data }) => setReport(data));
  }, [month]);

  // Group by YYYY-MM
  const byMonth = (rows || []).reduce((acc, r) => {
    const m = (r.date || "").slice(0, 7); if (!m) return acc;
    if (!acc[m]) acc[m] = { month: m, count: 0, sales: 0, commission: 0 };
    acc[m].count += 1;
    acc[m].sales += r.sale_amount || 0;
    if (r.status === "approved" || r.status === "paid") acc[m].commission += r.commission_amount || 0;
    return acc;
  }, {});
  const monthRollups = Object.values(byMonth).sort((a, b) => b.month.localeCompare(a.month));

  return (
    <div className="space-y-8" data-testid="commissions-pane">
      {/* Monthly snapshot */}
      <div className="border border-[#C9A055]/30 bg-[#0a0a0a] p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055]">Snapshot</div>
            <input type="text" value={month} onChange={(e) => setMonth(e.target.value)}
              placeholder="YYYY-MM" className="stc-input mt-1 w-32" data-testid="comm-month" />
          </div>
          {report && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-4 text-center flex-1">
              <KV label="Scans" v={report.total_scans} />
              <KV label="Sales" v={`$${(report.total_sales || 0).toFixed(2)}`} />
              <KV label="Commission" v={`$${(report.commission_total || 0).toFixed(2)}`} hl />
              <KV label="Top piece" v={report.top_artwork || "—"} />
            </div>
          )}
        </div>
      </div>

      {/* Month rollup */}
      <div>
        <h3 className="font-display text-xl text-[#F4F1EA] mb-3">By Month</h3>
        {monthRollups.length === 0 ? <p className="text-[#8a857d] italic">No commission activity yet.</p> : (
          <div className="border border-white/10">
            <table className="w-full text-sm">
              <thead className="bg-[#0a0a0a] text-[#8a857d] font-ui text-[9px] tracking-[0.22em] uppercase">
                <tr><th className="p-3 text-left">Month</th><th className="p-3 text-left">Rows</th><th className="p-3 text-left">Sales</th><th className="p-3 text-left">Commission</th></tr>
              </thead>
              <tbody>
                {monthRollups.map(m => (
                  <tr key={m.month} className="border-t border-white/5">
                    <td className="p-3 text-[#F2EFE8] font-mono">{m.month}</td>
                    <td className="p-3 text-[#B5B0A7]">{m.count}</td>
                    <td className="p-3 text-[#E8E4DC]">${m.sales.toFixed(2)}</td>
                    <td className="p-3 text-[#C9A055]">${m.commission.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h3 className="font-display text-xl text-[#F4F1EA] mb-3">All Activity</h3>
        {rows == null ? <Spinner /> : rows.length === 0 ?
          <Empty icon={<DollarSign size={28} />} title="No commission activity yet"
            body="Once your QR codes start being scanned and converting, commissions will appear here." />
          : (
            <div className="border border-white/10">
              <table className="w-full text-sm">
                <thead className="bg-[#0a0a0a] text-[#8a857d] font-ui text-[9px] tracking-[0.22em] uppercase">
                  <tr><th className="p-3 text-left">Source</th><th className="p-3 text-left">Sale</th><th className="p-3 text-left">Rate</th><th className="p-3 text-left">Commission</th><th className="p-3 text-left">Status</th><th className="p-3 text-left">Date</th></tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="p-3 text-[#F2EFE8]">{r.source}</td>
                      <td className="p-3 text-[#F2EFE8]">${parseFloat(r.sale_amount || 0).toFixed(2)}</td>
                      <td className="p-3 text-[#B5B0A7]">{((r.commission_rate || 0) * 100).toFixed(1)}%</td>
                      <td className="p-3 text-[#C9A055]">${parseFloat(r.commission_amount || 0).toFixed(2)}</td>
                      <td className="p-3 text-[12px] uppercase">{r.status}</td>
                      <td className="p-3 text-[12px] text-[#8a857d]">{r.date ? new Date(r.date).toLocaleDateString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      <div>
        <h3 className="font-display text-xl text-[#F4F1EA] mb-3">Payout History</h3>
        {payouts.length === 0 ? <p className="text-[#8a857d] italic">No payouts yet.</p> : (
          <div className="space-y-2">
            {payouts.map(p => (
              <div key={p.id} className="flex justify-between items-center border-b border-white/5 py-3 text-sm">
                <div>
                  <div className="text-[#F4F1EA]">{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : new Date(p.created_at).toLocaleDateString()}{p.payment_method ? ` · ${p.payment_method}` : ""}</div>
                  {p.payment_reference && <div className="text-[11px] text-[#8a857d] font-mono">{p.payment_reference}</div>}
                  {p.notes && <div className="text-[12px] text-[#B5B0A7]">{p.notes}</div>}
                </div>
                <div className="font-display text-2xl text-[#C9A055]">${(p.amount_paid || p.amount || 0).toFixed(2)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
function KV({ label, v, hl }) {
  return (
    <div>
      <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d]">{label}</div>
      <div className={`font-display text-2xl ${hl ? "text-[#C9A055]" : "text-[#F4F1EA]"} mt-1`}>{v}</div>
    </div>
  );
}

function SettingsPane({ onSaved }) {
  const [me, setMe] = useState(null);
  useEffect(() => { http.get("/partner/me").then(({ data }) => setMe(data)); }, []);
  if (!me) return <Spinner />;
  const set = (k, v) => setMe({ ...me, [k]: v });
  const save = async () => {
    try {
      await http.patch("/partner/me", {
        business_name: me.business_name, contact_name: me.name,
        phone: me.phone, payment_method: me.payment_method,
        payment_handle: me.payment_handle, payment_notes: me.payment_notes,
      });
      toast.success("Saved."); onSaved?.();
    } catch { toast.error("Save failed."); }
  };
  return (
    <div className="max-w-2xl space-y-4" data-testid="settings-pane">
      <h3 className="font-display text-xl text-[#F4F1EA]">Profile</h3>
      <FieldRow label="Business name"><input className="stc-input w-full" value={me.business_name || ""} onChange={e => set("business_name", e.target.value)} /></FieldRow>
      <FieldRow label="Contact name"><input className="stc-input w-full" value={me.name || ""} onChange={e => set("name", e.target.value)} /></FieldRow>
      <FieldRow label="Phone"><input className="stc-input w-full" value={me.phone || ""} onChange={e => set("phone", e.target.value)} /></FieldRow>
      <h3 className="font-display text-xl text-[#F4F1EA] pt-4">Payment Method</h3>
      <Sel label="Method" value={me.payment_method || ""} onChange={v => set("payment_method", v)}
        opts={[["", "Select…"], ["paypal", "PayPal"], ["venmo", "Venmo"], ["cashapp", "Cash App"], ["check", "Check"], ["other", "Other"]]} />
      <FieldRow label="Handle / Email / Address"><input className="stc-input w-full" value={me.payment_handle || ""} onChange={e => set("payment_handle", e.target.value)} /></FieldRow>
      <FieldRow label="Notes"><input className="stc-input w-full" value={me.payment_notes || ""} onChange={e => set("payment_notes", e.target.value)} /></FieldRow>
      <button onClick={save} className="btn-gold" data-testid="settings-save">Save Profile</button>
    </div>
  );
}

// --- shared bits -----------------------------------------------------------
function FieldRow({ label, children }) {
  return (
    <label className="block">
      <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mb-1">{label}</div>
      {children}
    </label>
  );
}
function Sel({ label, value, onChange, opts }) {
  return (
    <FieldRow label={label}>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="stc-input w-full">
        {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </FieldRow>
  );
}
function Empty({ icon, title, body }) {
  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-12 text-center">
      <div className="text-[#C9A055] mb-3 inline-block">{icon}</div>
      <div className="font-display text-2xl text-[#F4F1EA] mb-1">{title}</div>
      <p className="font-body text-[14px] text-[#B5B0A7] max-w-md mx-auto">{body}</p>
    </div>
  );
}
function RowList({ items, render, empty, testid }) {
  if (!items?.length) return <p className="text-[#8a857d] italic">{empty}</p>;
  return (
    <div className="space-y-2">
      {items.map((it) => (
        <div key={it.id} data-testid={`${testid}-${it.id}`} className="border border-white/10 bg-[#0a0a0a] p-4">
          {render(it)}
        </div>
      ))}
    </div>
  );
}
